import dayjs from "dayjs";
import { RECENT_PAST_WINDOW_DAYS } from "./rollover";

/**
 * Hard cutoff for shift records — anything older is considered out-of-scope for the Robots page.
 * Uses dayjs for calendar-accurate month subtraction (Date arithmetic with `4 * 30 * 86400000` is
 * an approximation that drifts ~2 days per cycle).
 */
export const SHIFT_LOOKBACK_MONTHS = 4;

export const shiftCutoffDate = (): Date => dayjs().startOf("day").subtract(SHIFT_LOOKBACK_MONTHS, "month").toDate();

/** Fetch filters on `startDate`, but the recent-ended window keys on `endDate` — a shift that
 *  ENDED inside the window may have STARTED up to its span earlier. Shifts here run ~1 day
 *  (noon-to-noon); 2 days covers stragglers. */
const SHIFT_SPAN_BUFFER_DAYS = 2;

/**
 * Cutoff for the Scheduling page — upcoming shifts PLUS the recent-past window (MC parity:
 * a past shift renders inline, editable, while it still carries unfinished jobs). Was
 * yesterday-only when the page rendered exclusively upcoming shifts; the recent-parity
 * feature shipped without widening it, which starved `recentPastShifts` in prod (the
 * offline fake ignores formulas, so fixtures masked it).
 */
export const schedulingShiftCutoffDate = (): Date =>
	dayjs()
		.startOf("day")
		.subtract(RECENT_PAST_WINDOW_DAYS + SHIFT_SPAN_BUFFER_DAYS, "day")
		.toDate();

/**
 * Visibility cutoff for dropoff STOPS — local start-of-today, NOT the shift window above.
 * MC parity: MC reads Shift Dropoffs through the locked AT view 'App View - Scheduled
 * Dropoffs' ("Sched Date is on or after today"), so stops disappear the day after their
 * shift's sched date even while the shift itself still renders in the recent-ended window.
 */
export const schedulingStopsCutoffDate = (): Date => dayjs().startOf("day").toDate();

/**
 * Whether a shift's dropoff stops fall inside the visibility window (start of `now`'s day —
 * the same boundary as `schedulingStopsCutoffDate`). A recent-past shift still renders inline and
 * stays editable for rollover, but its stops are hidden the day after its Sched Date. The
 * "Add dropoff" affordance is gated on this: a stop added to a shift whose stops are already
 * hidden would be created, then vanish on the next `schedDate >= today` refetch (customer report).
 */
export function shiftStopsVisible(scheduledDate: Date | undefined, now: Date): boolean {
	if (!scheduledDate) return false;
	return dayjs(scheduledDate).startOf("day").valueOf() >= dayjs(now).startOf("day").valueOf();
}
